// skipcq: JS-W1028
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { getDownloadURL, ref } from 'firebase/storage';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Paper from '@mui/material/Paper';

import DashboardLayout from '../../components/layout/DashboardLayout';
import { fetchAllBreweryClaimsForAdmin } from '../../services/breweryClaims';
import { functions, storage } from '../../utils/firebase';

const REVIEW_CLAIM_CALLABLE = 'callableBreweryReviewBreweryClaim-reviewBreweryClaim';

const CLAIM_STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'denied', label: 'Denied' },
];

function normalizedClaimStatus(row) {
  return typeof row.status === 'string' ? row.status : 'pending';
}

function formatClaimTimestamp(value) {
  if (!value || typeof value.toDate !== 'function') {
    return '—';
  }
  try {
    return value.toDate().toLocaleString();
  } catch {
    return '—';
  }
}

function formatCityState(cityRaw, stateRaw) {
  const city = typeof cityRaw === 'string' ? cityRaw.trim() : '';
  const state = typeof stateRaw === 'string' ? stateRaw.trim() : '';
  if (city && state) {
    return `${city}, ${state}`;
  }
  return city || state || '—';
}

function formatClaimant(row) {
  const name = typeof row.requesterName === 'string' ? row.requesterName.trim() : '';
  const email = typeof row.requesterEmail === 'string' ? row.requesterEmail.trim() : '';
  const uid = typeof row.userId === 'string' ? row.userId.trim() : '';
  const parts = [];
  if (name) {
    parts.push(name);
  }
  if (email) {
    parts.push(email);
  }
  if (!name && !email && uid) {
    parts.push(uid);
  }
  return parts.length > 0 ? parts.join(' · ') : '—';
}

function ClaimBreweryTableHead() {
  return (
    <TableHead>
      <TableRow>
        <TableCell>Brewery</TableCell>
        <TableCell>Location</TableCell>
        <TableCell>Claimant</TableCell>
        <TableCell>Claim date</TableCell>
        <TableCell>Proof</TableCell>
        <TableCell>Account status</TableCell>
      </TableRow>
    </TableHead>
  );
}

/**
 * @param {{
 *   rowId: string,
 *   statusRaw: string,
 *   selectDisabled: boolean,
 *   isUpdating: boolean,
 *   menuForStatus: string[],
 *   onSelectChange: (event: { target: { value: string } }) => void,
 * }} props
 */
function ClaimAccountStatusCell({
  rowId,
  statusRaw,
  selectDisabled,
  isUpdating,
  menuForStatus,
  onSelectChange,
}) {
  return (
    <TableCell sx={{ minWidth: 160 }}>
      <FormControl size="small" fullWidth disabled={selectDisabled}>
        <InputLabel id={`status-${rowId}`}>Account status</InputLabel>
        <Select
          labelId={`status-${rowId}`}
          label="Account status"
          value={statusRaw}
          onChange={onSelectChange}
        >
          {menuForStatus.map((option) => (
            <MenuItem key={option} value={option}>
              {option.charAt(0).toUpperCase() + option.slice(1)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {isUpdating ? <CircularProgress size={20} sx={{ mt: 1 }} /> : null}
    </TableCell>
  );
}

/**
 * @param {{
 *   row: Record<string, unknown> & { id: string },
 *   proofUrl: string | null,
 *   updatingRowId: string | null,
 *   onStatusChange: (claimId: string, status: string) => void,
 * }} props
 */
function ClaimBreweryTableRow({ row, proofUrl, updatingRowId, onStatusChange }) {
  const breweryId = typeof row.breweryId === 'string' ? row.breweryId : '';
  const breweryName =
    typeof row.breweryName === 'string' && row.breweryName.trim().length > 0
      ? row.breweryName.trim()
      : breweryId;
  const statusRaw = normalizedClaimStatus(row);
  const isUpdating = updatingRowId === row.id;

  const handleSelectChange = useCallback(
    (event) => {
      const nextStatus = event.target.value;
      if (nextStatus === statusRaw) {
        return;
      }
      onStatusChange(row.id, nextStatus);
    },
    [onStatusChange, row.id, statusRaw],
  );

  const menuForStatus =
    statusRaw === 'approved'
      ? ['approved']
      : statusRaw === 'denied'
        ? ['denied', 'approved']
        : ['pending', 'approved', 'denied'];

  const selectDisabled = isUpdating || statusRaw === 'approved';

  return (
    <TableRow key={row.id}>
      <TableCell>
        {breweryId ? (
          <RouterLink to={`/brewery/${breweryId}`}>{breweryName}</RouterLink>
        ) : (
          breweryName
        )}
      </TableCell>
      <TableCell>{formatCityState(row.breweryCity, row.breweryState)}</TableCell>
      <TableCell>{formatClaimant(row)}</TableCell>
      <TableCell>{formatClaimTimestamp(row.createdAt)}</TableCell>
      <TableCell>
        {proofUrl ? (
          <a href={proofUrl} target="_blank" rel="noopener noreferrer">
            View document
          </a>
        ) : (
          '—'
        )}
      </TableCell>
      <ClaimAccountStatusCell
        rowId={row.id}
        statusRaw={statusRaw}
        selectDisabled={selectDisabled}
        isUpdating={isUpdating}
        menuForStatus={menuForStatus}
        onSelectChange={handleSelectChange}
      />
    </TableRow>
  );
}

function ClaimBreweryTable({ rows, proofUrlById, updatingRowId, onStatusChange }) {
  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <ClaimBreweryTableHead />
        <TableBody>
          {rows.map((row) => (
            <ClaimBreweryTableRow
              key={row.id}
              row={row}
              proofUrl={proofUrlById[row.id] ?? null}
              updatingRowId={updatingRowId}
              onStatusChange={onStatusChange}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ClaimBreweriesStatusFilter({ statusFilter, onStatusFilterChange }) {
  return (
    <FormControl size="small" sx={{ minWidth: 200 }}>
      <InputLabel id="claimed-breweries-status-filter-label">Status</InputLabel>
      <Select
        labelId="claimed-breweries-status-filter-label"
        label="Status"
        value={statusFilter}
        onChange={onStatusFilterChange}
      >
        {CLAIM_STATUS_FILTER_OPTIONS.map((option) => (
          <MenuItem key={option.value || 'all'} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function ClaimedBreweriesContent() {
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [proofUrlById, setProofUrlById] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [updatingRowId, setUpdatingRowId] = useState(null);
  const reviewClaimCallable = useMemo(
    () => httpsCallable(functions, REVIEW_CLAIM_CALLABLE),
    [],
  );

  const loadClaims = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const fetchedRows = await fetchAllBreweryClaimsForAdmin();
      setRows(fetchedRows);

      const proofEntries = await Promise.all(
        fetchedRows.map(async (claimRow) => {
          const path = claimRow.proofStoragePath;
          if (typeof path !== 'string' || path.length === 0) {
            return [claimRow.id, null];
          }
          try {
            const url = await getDownloadURL(ref(storage, path));
            return [claimRow.id, url];
          } catch {
            return [claimRow.id, null];
          }
        }),
      );
      const nextMap = {};
      for (const [claimId, url] of proofEntries) {
        nextMap[claimId] = url;
      }
      setProofUrlById(nextMap);
    } catch (error) {
      const message =
        typeof error?.message === 'string' ? error.message : 'Failed to load brewery claims.';
      setLoadError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClaims();
  }, [loadClaims]);

  const filteredRows = useMemo(() => {
    if (statusFilter === '') {
      return rows;
    }
    return rows.filter((row) => normalizedClaimStatus(row) === statusFilter);
  }, [rows, statusFilter]);

  const handleStatusFilterChange = useCallback((event) => {
    setStatusFilter(event.target.value);
  }, []);

  const handleStatusChange = useCallback(
    async (claimId, status) => {
      setActionError('');
      setUpdatingRowId(claimId);
      try {
        await reviewClaimCallable({ claimId, status });
        await loadClaims();
      } catch (error) {
        const code = typeof error?.code === 'string' ? error.code : '';
        const message = typeof error?.message === 'string' ? error.message : 'Update failed.';
        setActionError(code ? `${code}: ${message}` : message);
      } finally {
        setUpdatingRowId(null);
      }
    },
    [loadClaims, reviewClaimCallable],
  );

  if (loading) {
    return (
      <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return <Alert severity="error">{loadError}</Alert>;
  }

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No brewery claims yet.
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      {actionError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {actionError}
        </Alert>
      ) : null}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 2,
          mb: 2,
        }}
      >
        <ClaimBreweriesStatusFilter
          statusFilter={statusFilter}
          onStatusFilterChange={handleStatusFilterChange}
        />
      </Box>
      {filteredRows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No claims match the selected status.
        </Typography>
      ) : (
        <ClaimBreweryTable
          rows={filteredRows}
          proofUrlById={proofUrlById}
          updatingRowId={updatingRowId}
          onStatusChange={handleStatusChange}
        />
      )}
    </Box>
  );
}

export default function ClaimedBreweriesPage() {
  return (
    <DashboardLayout title="Claimed Breweries">
      <Box sx={{ px: { xs: 2, sm: 3 } }}>
        <ClaimedBreweriesContent />
      </Box>
    </DashboardLayout>
  );
}
